// ./src/components/layouts/DashboardLayout/MoreView.tsx
// Full-screen "More" menu view for mobile devices.
// Replaces the mobile sidebar with a native-feeling settings/menu screen.
// Shows remaining navigation items (not in bottom tab bar),
// account menu items, and sign out.

import React, { useMemo, useCallback, useState } from "react";
import {
  Box,
  Group,
  Text,
  Stack,
  Paper,
  Avatar,
  ScrollArea,
  UnstyledButton,
  Divider,
  Loader,
  useMantineTheme,
} from "@mantine/core";
import { useTranslation } from "react-i18next";

import { SiteMapItem } from "@/config";

import { User } from "@/utils/api/UserService";

import { AppIcon } from "@/components/generic/AppIcon";

import { DEFAULT_ICON_COLOR, getIconBgColor } from "./constants";

// ============================================
// Types
// ============================================

export interface MoreViewProps {
  /** Navigation items to display (already sliced — excludes bottom tab items) */
  navigationItems: SiteMapItem[];

  /** Current user */
  user: User | null | undefined;

  /** Whether user is account owner */
  isOwner: boolean;

  /** App version string */
  appVersion: string;

  /** Current route path — used to detect same-path taps */
  currentPath: string;

  /** Navigation handler */
  onNavigate: (path: string) => void;

  /** Close the More view without navigating (e.g. same-path tap) */
  onClose: () => void;

  /** Sign out handler */
  onSignOut: () => void;

  /** Translation helper for nav item names */
  getTranslatedName: (translationKey?: string, fallback?: string) => string;

  /** Translation helper for nav item subtitles */
  getTranslatedSubtitle: (translationKey?: string, fallback?: string) => string;
}

// ============================================
// Constants
// ============================================

const ICON_CIRCLE_SIZE = 40;
const ICON_SIZE = 20;

/** Account menu items — derived from sitemap hidden items */
const ACCOUNT_PATHS = {
  profile: "/account/profile",
  settings: "/account/settings",
  subscription: "/account/subscription",
};

// ============================================
// CSS class names for tap animation
// ============================================

const TAP_STYLES = `
  .more-menu-item:active {
    transform: scale(0.97);
  }
  .more-menu-item-navigating {
    animation: more-menu-pulse 1s ease-in-out infinite;
  }
  @keyframes more-menu-pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.7; }
  }
`;

// ============================================
// Sub-components
// ============================================

interface MenuItemRowProps {
  iconName: string;
  iconColor: string;
  title: string;
  subtitle?: string;
  onClick: () => void;
  /** This item was tapped and navigation is in progress */
  isNavigating?: boolean;
  /** Another item is navigating — dim this one */
  isOtherNavigating?: boolean;
}

const MenuItemRow: React.FC<MenuItemRowProps> = ({
  iconName,
  iconColor,
  title,
  subtitle,
  onClick,
  isNavigating = false,
  isOtherNavigating = false,
}) => {
  const theme = useMantineTheme();

  return (
    <UnstyledButton
      onClick={onClick}
      w="100%"
      disabled={isNavigating || isOtherNavigating}
      className={`more-menu-item ${isNavigating ? "more-menu-item-navigating" : ""}`}
      style={{
        WebkitTapHighlightColor: "transparent",
        opacity: isOtherNavigating ? 0.5 : 1,
        transition: "opacity 150ms ease, transform 100ms ease",
      }}
    >
      <Group
        gap="sm"
        wrap="nowrap"
        px="md"
        py="sm"
        bg={isNavigating ? "brand.0" : undefined}
        style={{
          borderRadius: theme.radius.sm,
          transition: "background-color 150ms ease",
        }}
      >
        {/* Icon Circle */}
        <Box
          w={ICON_CIRCLE_SIZE}
          h={ICON_CIRCLE_SIZE}
          style={{
            backgroundColor: getIconBgColor(iconColor),
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <AppIcon iconName={iconName} size={ICON_SIZE} color={iconColor} />
        </Box>

        {/* Text Content */}
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Text
            fw={600}
            size="sm"
            c={isNavigating ? "brand.7" : "neutral.8"}
            lineClamp={1}
            style={{ transition: "color 150ms ease" }}
          >
            {title}
          </Text>
          {subtitle && (
            <Text size="xs" c="neutral.5" lineClamp={1}>
              {subtitle}
            </Text>
          )}
        </Box>

        {/* Chevron or Loading Indicator */}
        {isNavigating ? (
          <Loader size={16} color={theme.colors.brand[6]} type="dots" />
        ) : (
          <AppIcon
            iconName="ChevronRight"
            size={18}
            color={theme.colors.neutral[4]}
          />
        )}
      </Group>
    </UnstyledButton>
  );
};

interface SectionHeaderProps {
  title: string;
}

const SectionHeader: React.FC<SectionHeaderProps> = ({ title }) => {
  return (
    <Text
      size="xs"
      fw={700}
      c="neutral.5"
      tt="uppercase"
      px="md"
      pt="lg"
      pb="xs"
      style={{ letterSpacing: "0.5px" }}
    >
      {title}
    </Text>
  );
};

interface UserCardProps {
  user: User;
  onNavigate: (path: string) => void;
  isNavigating?: boolean;
  isOtherNavigating?: boolean;
}

const UserCard: React.FC<UserCardProps> = ({
  user,
  onNavigate,
  isNavigating = false,
  isOtherNavigating = false,
}) => {
  const theme = useMantineTheme();

  const userInitials =
    user.username
      ?.split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase() || "?";

  return (
    <UnstyledButton
      onClick={() => onNavigate(ACCOUNT_PATHS.profile)}
      w="100%"
      px="md"
      disabled={isNavigating || isOtherNavigating}
      className={`more-menu-item ${isNavigating ? "more-menu-item-navigating" : ""}`}
      style={{
        WebkitTapHighlightColor: "transparent",
        opacity: isOtherNavigating ? 0.5 : 1,
        transition: "opacity 150ms ease, transform 100ms ease",
      }}
    >
      <Paper
        withBorder
        p="md"
        radius="md"
        bg={isNavigating ? "brand.0" : undefined}
        style={{ transition: "background-color 150ms ease" }}
      >
        <Group gap="sm" wrap="nowrap">
          <Avatar size={44} color="brand" radius="md">
            {userInitials}
          </Avatar>
          <Box style={{ flex: 1, minWidth: 0 }}>
            <Text fw={700} size="md" c="neutral.8" truncate>
              {user.username}
            </Text>
            <Text size="sm" c="neutral.5" truncate>
              {user.email}
            </Text>
          </Box>
          {isNavigating ? (
            <Loader size={16} color={theme.colors.brand[6]} type="dots" />
          ) : (
            <AppIcon
              iconName="ChevronRight"
              size={18}
              color={theme.colors.neutral[4]}
            />
          )}
        </Group>
      </Paper>
    </UnstyledButton>
  );
};

// ============================================
// Main Component
// ============================================

export const MoreView: React.FC<MoreViewProps> = ({
  navigationItems,
  user,
  isOwner,
  appVersion,
  currentPath,
  onNavigate,
  onClose,
  onSignOut,
  getTranslatedName,
  getTranslatedSubtitle,
}) => {
  const { t } = useTranslation();
  const theme = useMantineTheme();

  // Track which path is currently navigating
  const [navigatingTo, setNavigatingTo] = useState<string | null>(null);

  // Separate standalone items from parent groups
  const { standaloneItems, parentGroups } = useMemo(() => {
    const standalone: SiteMapItem[] = [];
    const parents: SiteMapItem[] = [];

    navigationItems.forEach((item) => {
      const hasVisibleChildren =
        Array.isArray(item.items) &&
        item.items.some((child) => child.visible !== false);

      if (hasVisibleChildren) {
        parents.push(item);
      } else if (item.url) {
        standalone.push(item);
      }
    });

    return { standaloneItems: standalone, parentGroups: parents };
  }, [navigationItems]);

  // Account items — filtered by role
  const accountItems = useMemo(() => {
    const items: Array<{ icon: string; iconColor: string; titleKey: string; subtitleKey: string; path: string }> = [
      {
        icon: "User",
        iconColor: theme.colors.neutral[6],
        titleKey: "pages.userProfilePage.name",
        subtitleKey: "pages.userProfilePage.subtitle",
        path: ACCOUNT_PATHS.profile,
      },
    ];

    if (isOwner) {
      items.push(
        {
          icon: "Settings",
          iconColor: theme.colors.neutral[5],
          titleKey: "pages.accountSettings.name",
          subtitleKey: "pages.accountSettings.subtitle",
          path: ACCOUNT_PATHS.settings,
        },
        {
          icon: "CreditCard",
          iconColor: theme.colors.brand[6],
          titleKey: "pages.subscription.name",
          subtitleKey: "pages.subscription.subtitle",
          path: ACCOUNT_PATHS.subscription,
        }
      );
    }

    return items;
  }, [isOwner, theme]);

  // Navigation handler — if tapping the already-active page, just close More
  const handleNavigate = useCallback(
    (path: string) => {
      if (navigatingTo) return; // Prevent double-tap

      // Special paths like "__support__" are never same-path
      if (path !== "__support__" && path === currentPath) {
        // Already on this page — just close the More view
        onClose();
        return;
      }

      setNavigatingTo(path);
      onNavigate(path);
    },
    [onNavigate, onClose, navigatingTo, currentPath]
  );

  // Render a single navigation item row
  const renderNavItem = useCallback(
    (item: SiteMapItem) => {
      const itemName = getTranslatedName(item.options?.translationKey, item.name);
      const itemSubtitle = getTranslatedSubtitle(item.options?.translationKey, item.subtitle);
      const iconColor = item.iconColor || DEFAULT_ICON_COLOR;
      const itemPath = item.url || "";

      return (
        <MenuItemRow
          key={item.url || item.name}
          iconName={item.icon || "Circle"}
          iconColor={iconColor}
          title={itemName}
          subtitle={itemSubtitle}
          onClick={() => item.url && handleNavigate(item.url)}
          isNavigating={navigatingTo === itemPath}
          isOtherNavigating={navigatingTo != null && navigatingTo !== itemPath}
        />
      );
    },
    [getTranslatedName, getTranslatedSubtitle, handleNavigate, navigatingTo]
  );

  return (
    <>
      {/* Inject tap animation styles */}
      <style>{TAP_STYLES}</style>

      <ScrollArea
        h="100%"
        scrollbarSize={6}
        style={{ backgroundColor: theme.colors.neutral[0] }}
      >
        <Stack gap={0} pb={100}>
          {/* User Card */}
          {user && (
            <Box pt="md" pb="xs">
              <UserCard
                user={user}
                onNavigate={handleNavigate}
                isNavigating={navigatingTo === ACCOUNT_PATHS.profile}
                isOtherNavigating={navigatingTo != null && navigatingTo !== ACCOUNT_PATHS.profile}
              />
            </Box>
          )}

          {/* Standalone Navigation Items */}
          {standaloneItems.length > 0 && (
            <Paper withBorder mx="md" mt="md" radius="md" py="xs">
              {standaloneItems.map((item, index) => (
                <React.Fragment key={item.url || item.name}>
                  {renderNavItem(item)}
                  {index < standaloneItems.length - 1 && (
                    <Divider mx="md" color="neutral.1" />
                  )}
                </React.Fragment>
              ))}
            </Paper>
          )}

          {/* Parent Groups (Reports, Settings, Team) */}
          {parentGroups.map((parent) => {
            const visibleChildren = (parent.items || []).filter(
              (child) => child.visible !== false
            );
            if (visibleChildren.length === 0) return null;

            const sectionName = getTranslatedName(
              parent.options?.translationKey,
              parent.name
            );

            return (
              <React.Fragment key={parent.name}>
                <SectionHeader title={sectionName} />
                <Paper withBorder mx="md" radius="md" py="xs">
                  {visibleChildren.map((child, index) => (
                    <React.Fragment key={child.url || child.name}>
                      {renderNavItem(child)}
                      {index < visibleChildren.length - 1 && (
                        <Divider mx="md" color="neutral.1" />
                      )}
                    </React.Fragment>
                  ))}
                </Paper>
              </React.Fragment>
            );
          })}

          {/* Account Section */}
          <SectionHeader title={t("pages.account.name", { defaultValue: "Account" })} />
          <Paper withBorder mx="md" radius="md" py="xs">
            {accountItems.map((item, index) => (
              <React.Fragment key={item.path}>
                <MenuItemRow
                  iconName={item.icon}
                  iconColor={item.iconColor}
                  title={t(item.titleKey)}
                  subtitle={t(item.subtitleKey, { defaultValue: "" })}
                  onClick={() => handleNavigate(item.path)}
                  isNavigating={navigatingTo === item.path}
                  isOtherNavigating={navigatingTo != null && navigatingTo !== item.path}
                />
                {index < accountItems.length - 1 && (
                  <Divider mx="md" color="neutral.1" />
                )}
              </React.Fragment>
            ))}
          </Paper>

          {/* Help & Support */}
          <SectionHeader title={t("navigation.support", { defaultValue: "Support" })} />
          <Paper withBorder mx="md" radius="md" py="xs">
            <MenuItemRow
              iconName="Help"
              iconColor={theme.colors.brand[6]}
              title={t("navigation.helpAndSupport")}
              subtitle=""
              onClick={() => handleNavigate("__support__")}
              isNavigating={navigatingTo === "__support__"}
              isOtherNavigating={navigatingTo != null && navigatingTo !== "__support__"}
            />
          </Paper>

          {/* Sign Out */}
          <Box px="md" pt="lg">
            <UnstyledButton
              onClick={onSignOut}
              w="100%"
              disabled={navigatingTo != null}
              className="more-menu-item"
              style={{
                WebkitTapHighlightColor: "transparent",
                opacity: navigatingTo != null ? 0.5 : 1,
                transition: "opacity 150ms ease, transform 100ms ease",
              }}
            >
              <Paper
                withBorder
                p="md"
                radius="md"
                style={{ borderColor: theme.colors.error[2] }}
                bg="error.0"
              >
                <Group gap="sm" justify="center">
                  <AppIcon
                    iconName="Logout"
                    size={18}
                    color={theme.colors.error[6]}
                  />
                  <Text fw={600} size="sm" c="error.6">
                    {t("navigation.logout")}
                  </Text>
                </Group>
              </Paper>
            </UnstyledButton>
          </Box>

          {/* App Version Footer */}
          <Stack align="center" gap={2} pt="lg" pb="md">
            <Text size="xs" c="neutral.4">
              Car Expenses {appVersion}
            </Text>
            <Text size="xs" c="neutral.4">
              © {new Date().getFullYear()} CarExpenses
            </Text>
          </Stack>
        </Stack>
      </ScrollArea>
    </>
  );
};